import {
  Button,
  Chip,
  Divider,
  InputAdornment,
  OutlinedInputProps,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { ChangeEvent, useState } from "react";
import { useDebouncedEffect } from "../../../App/State/Hooks/useDebouncedEffect";
import Modal from "../../../App/Components/Modal/Modal";
import { DeleteCandidateStateType } from "./State/DeleteCandidateState";
import {
  DeleteCandidatesModalBody,
  DeleteCandidatesModalFooter,
} from "./Components/DeleteCandidatesModalBody";
import { Grid3x3 } from "@mui/icons-material";

// https://www.geeksforgeeks.org/how-to-validate-guid-globally-unique-identifier-using-regular-expression/
const guidRegex = /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/i;
const isValidGuid = (id: string) => guidRegex.test(id);

export const DirectDelete = ({
  state: {
    effects: { handleDeleteCandidates, handleCloseDeleteModalWithFailedDeletes },
    getters: {
      candidatesToDelete,
      failedDeletes,
      isDeleteModalOpen,
      isDeleteCandidatesLoading,
      responseMessage,
    },
    setters: { setCandidatesToDelete, setIsDeleteModalOpen },
  },
}: {
  state: DeleteCandidateStateType;
}) => {
  const [inputValue, setInputValue] = useState("");

  let buttonText = "Delete Candidates";
  if (candidatesToDelete.length > 1) {
    buttonText = `Delete ${candidatesToDelete.length} Candidates`;
  }
  if (candidatesToDelete.length === 1) {
    buttonText = "Delete Candidate";
  }

  const handleInputValueChange = (e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value);
  const handleClearAll = () => setCandidatesToDelete([]);
  const handleOpenModal = () => setIsDeleteModalOpen(true);
  const handleCloseModal = () => setIsDeleteModalOpen(false);
  const handleRemoveIdToDelete = (id: string) =>
    setCandidatesToDelete((ids) => ids.filter((savedId) => savedId !== id));

  const addIds = (input: string) => {
    const newIds = input.split(/[\s,]+/).filter((id) => isValidGuid(id) && !candidatesToDelete.includes(id));
    if (newIds.length === 0) {
      return;
    }
    setCandidatesToDelete((ids) => [...ids, ...newIds]);
    setInputValue("");
  };

  useDebouncedEffect(
    () => {
      addIds(inputValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [inputValue],
    500,
  );

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
      <TextField
        sx={{ width: 300 }}
        label="Enter Candidate ID(s)"
        variant="outlined"
        value={inputValue}
        onChange={handleInputValueChange}
        placeholder="Type or paste Candidate IDs"
        InputProps={
          {
            "data-test": "candidate-ids-input",
            startAdornment: (
              <InputAdornment position="start">
                <Grid3x3 fontSize="small" />
              </InputAdornment>
            ),
          } as Partial<OutlinedInputProps>
        }
      />
      <Button
        data-test="clear-delete-candidates"
        color="secondary"
        disabled={candidatesToDelete.length === 0}
        onClick={handleClearAll}
      >
        Clear All
      </Button>
      <Button
        data-test="delete-candidates-button"
        variant="contained"
        color="error"
        sx={{ ml: "auto" }}
        disabled={candidatesToDelete.length === 0}
        onClick={handleOpenModal}
      >
        {buttonText}
      </Button>
      {candidatesToDelete.length > 0 && (
        <Box sx={{ width: "100%" }}>
          <Divider textAlign="left">
            <Typography data-test="total-candidates" variant="caption">
              Total Candidates: {candidatesToDelete.length}
            </Typography>
          </Divider>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {candidatesToDelete.map((id) => (
              <Chip
                data-test={`candidate-to-delete-${id}`}
                key={id}
                label={id}
                onDelete={() => handleRemoveIdToDelete(id)}
              />
            ))}
          </Box>
        </Box>
      )}
      <Modal
        title="Confirm Delete"
        dataTestId="delete-candidate"
        isOpen={isDeleteModalOpen}
        handleCloseModal={handleCloseModal}
        content={
          <DeleteCandidatesModalBody
            candidatesToDelete={candidatesToDelete}
            failedDeletes={failedDeletes}
            responseMessage={responseMessage}
          />
        }
        footerContent={
          <DeleteCandidatesModalFooter
            failedDeletes={failedDeletes}
            disabled={candidatesToDelete.length === 0 || isDeleteCandidatesLoading}
            onSend={handleDeleteCandidates}
            onClose={handleCloseDeleteModalWithFailedDeletes}
          />
        }
      />
    </Stack>
  );
};
